import { MainCard } from '@/components/main-card';
import { cn } from '@/lib/utils';
import { getScaledValue, getUnitLabel } from '@/utils/numberFormat';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CARD_ACCENTS } from '../constants';
import { InsuranceMetric, InsuranceStats } from '../model';

interface InsuranceStatCardsProps {
  data?: InsuranceStats;
  isLoading?: boolean;
  className?: string;
  /** Strelka bosilganda ko'rsatkich viloyatlar kesimida ochiladi.
   *  Viloyat tanlangan bo'lsa berilmaydi — strelka ham chiqmaydi. */
  onMetricOpen?: (metric: InsuranceMetric, title: string) => void;
}

export const InsuranceStatCards = ({
  data,
  isLoading,
  className,
  onMetricOpen,
}: InsuranceStatCardsProps) => {
  const { t } = useTranslation();

  /** Sonlar aniq ko'rsatiladi — hozirgi qiymatlar kichik. */
  const count = (value?: number) => ({
    value: Number(value || 0),
    unit: t('ta'),
  });

  /** Maydon va summa ming/mln/trln ko'rinishida qisqartiriladi. */
  const scaled = (
    value: string | number | undefined,
    type: 'ga' | 'currency',
  ) => {
    const numeric = Number(value || 0);
    return {
      value: getScaledValue(numeric),
      unit: getUnitLabel(numeric, type),
    };
  };

  const cards = [
    {
      metric: 'active_application_count' as InsuranceMetric,
      title: t('insurance_active_applications'),
      ...count(data?.active_application_count),
    },
    {
      metric: 'concluded_contract_count' as InsuranceMetric,
      title: t('insurance_concluded_contracts'),
      ...count(data?.concluded_contract_count),
    },
    {
      metric: 'completed_insured_area_ha' as InsuranceMetric,
      title: t('insurance_insured_area'),
      ...scaled(data?.completed_insured_area_ha, 'ga'),
    },
    {
      metric: 'completed_insurance_amount_total' as InsuranceMetric,
      title: t('insurance_total_amount'),
      ...scaled(data?.completed_insurance_amount_total, 'currency'),
    },
    {
      metric: 'monitored_area_ha' as InsuranceMetric,
      title: t('insurance_monitored_area'),
      ...scaled(data?.monitored_area_ha, 'ga'),
    },
    {
      metric: 'issued_policy_count' as InsuranceMetric,
      title: t('insurance_issued_policies'),
      ...count(data?.issued_policy_count),
    },
    {
      metric: 'confirmed_claim_count' as InsuranceMetric,
      title: t('insurance_confirmed_claims'),
      ...count(data?.confirmed_claim_count),
    },
    {
      metric: 'canceled_application_count' as InsuranceMetric,
      title: t('insurance_canceled_applications'),
      ...count(data?.canceled_application_count),
    },
  ];

  return (
    <div className={cn('grid grid-cols-4 gap-4', className)}>
      {cards.map((card, index) => (
        <MainCard
          key={card.title}
          title={card.title}
          value={card.value}
          unit={card.unit}
          isLoading={isLoading}
          leftBorderColor={CARD_ACCENTS[index % CARD_ACCENTS.length]}
          Icon={onMetricOpen ? ArrowRight : undefined}
          /* Strelka ortidagi oq fon kerak emas — faqat ramka qoladi. */
          iconBgColor="bg-transparent"
          iconColor="text-muted-foreground"
          onClick={
            onMetricOpen
              ? () => onMetricOpen(card.metric, card.title)
              : undefined
          }
        />
      ))}
    </div>
  );
};
